import type { BlockId } from '@unifyapps/defs/types/block';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import type { StoreApi, UseBoundStore } from 'zustand';
import { StandardPublicPageSlugs } from '../../../const/noCodeTemplates';
import { getInterfaceRecordSelectors } from './getInterfaceRecordSelectors';
import type { InterfaceStoreType } from './types';

type InterfaceStore = UseBoundStore<
  StoreApi<InterfaceStoreType> & {
    subscribe: <T>(
      selector: (state: InterfaceStoreType) => T,
      listener: (selectedState: T, previousSelectedState: T) => void,
      options?: { equalityFn?: (a: T, b: T) => boolean },
    ) => () => void;
  }
>;

const getPage = (state: InterfaceStoreType, interfacePageId?: string) =>
  interfacePageId ? state.interfacePages[interfacePageId] : state.activeInterfacePage;

export const getInterfaceStoreSelectors = (store: InterfaceStore) => {
  const storeSelectors = {
    useMode: () => store((state) => state.mode),
    useActiveInterfacePage: () => store((state) => state.activeInterfacePage),
    useActiveInterfacePageId: () => store((state) => state.activeInterfacePageId),
    usePageScopeKey: () => store((state) => state.pageScopeKey),
    useInstanceId: () => store((state) => state.instanceId),
    usePages: () => store((state) => state.interfacePages),
    useActions: () => store.getState().actions,
    useIsPublic: () => store((state) => state.isPublic),
  };

  const recordSelectors = getInterfaceRecordSelectors(store);

  const pageSelectors = {
    useId: (interfacePageId?: string) => store((state) => getPage(state, interfacePageId)?.id),
    useVersion: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.version),
    useName: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.name),
    useSlug: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.slug),
    useDocumentTitle: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.documentTitle),
    useStatusBar: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.statusBar),
    useNavigationBar: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.navigationBar),
    useComponentType: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.componentType),
    useIsPublicPage: (interfacePageId?: string) =>
      store((state) => {
        const page = getPage(state, interfacePageId);
        const pageSlug = page?.properties.slug;
        // for login/auth pages, publicAcess is not set, they should be treated as public pages
        if (pageSlug && StandardPublicPageSlugs.includes(pageSlug)) {
          return true;
        }
        return Boolean(page?.properties.publicAccess);
      }),
    useInputSchema: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.inputSchema),
    useInteractions: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.interactions),
    useOutput: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.output),
    useFolders: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.folders),
    useDetails: (interfacePageId?: string) => store((state) => getPage(state, interfacePageId)),
    useDataSources: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.dataSources),
    usePageVariables: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.pageVariables),
    usePageFunctions: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.pageFunctions),
    useEvents: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.events),
    useInboundEvents: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.inboundEvents),
    usePageVariableById: (pageVariableId: string) =>
      store((state) => state.activeInterfacePage?.properties.pageVariables?.[pageVariableId]),
    usePageFunctionById: (pageFunctionId: string, interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.pageFunctions?.[pageFunctionId]),
    useLayout: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.layout),
    useExportLayout: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.exportLayout),
    useLoader: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.loader),
    useFlags: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.flags),
    useWalkthrough: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.walkthrough),
    metadata: {
      useDetails: (interfacePageId?: string) =>
        store((state) => getPage(state, interfacePageId)?.properties.metadata),
      useDependencies: (interfacePageId?: string) =>
        store((state) => getPage(state, interfacePageId)?.properties.metadata?.dependencies),
      useDependencyById: (interfacePageId?: string) =>
        store((state) => getPage(state, interfacePageId)?.properties.metadata?.dependencyById),
    },
  };

  const walkthroughSelectors = {
    useSettings: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.walkthrough?.settings),
    useSteps: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.walkthrough?.steps),
    useStepById: (stepId: string | null, interfacePageId?: string) =>
      store((state) => {
        const steps = getPage(state, interfacePageId)?.properties.walkthrough?.steps;
        return steps?.find((step) => step.id === stepId);
      }),
    useStepsForPage: (targetPageId: string, interfacePageId?: string) =>
      store((state) => {
        const steps = getPage(state, interfacePageId)?.properties.walkthrough?.steps;
        return steps?.filter((step) => step.interfacePageId === targetPageId);
      }),
    useStatus: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.walkthrough?.settings?.status),
    useTargetPageId: (interfacePageId?: string) =>
      store(
        (state) =>
          getPage(state, interfacePageId)?.properties.walkthrough?.settings?.targetInterfacePageId,
      ),
    useIsActive: (interfacePageId?: string) =>
      store(
        (state) =>
          getPage(state, interfacePageId)?.properties.walkthrough?.settings?.status === 'active',
      ),
    usePriority: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.walkthrough?.settings?.priority),
    useDeviceDetails: (interfacePageId?: string) =>
      store(
        (state) =>
          getPage(state, interfacePageId)?.properties.walkthrough?.settings?.deviceDetails ??
          DeviceVariantType.DESKTOP,
      ),
  };

  const blockSelectors = {
    useFromBase: ({ blockId, interfacePageId }: { interfacePageId?: string; blockId: BlockId }) => {
      const _blockId = blockId || '';
      return store((state) => getPage(state, interfacePageId)?.properties.blocks?.[_blockId]);
    },
    useFromDevice: ({
      device,
      blockId,
      interfacePageId,
    }: {
      interfacePageId?: string;
      device: DeviceVariantType;
      blockId: BlockId;
    }) => {
      const _blockId = blockId || '';
      return store(
        (state) =>
          getPage(state, interfacePageId)?.properties.devices?.[device]?.blocks?.[_blockId],
      );
    },
  };

  const blocksSelectors = {
    useFromBaseDevice: (interfacePageId?: string) =>
      store((state) => getPage(state, interfacePageId)?.properties.blocks),
    useFromDevice: ({
      deviceVariant,
      interfacePageId,
    }: {
      interfacePageId?: string;
      deviceVariant: DeviceVariantType;
    }) =>
      store(
        (state) => getPage(state, interfacePageId)?.properties.devices?.[deviceVariant]?.blocks,
      ),
  };

  return {
    storeSelectors,
    recordSelectors,
    pageSelectors,
    walkthroughSelectors,
    blockSelectors,
    blocksSelectors,
  };
};

export const createInterfaceStoreSelectors = (store: InterfaceStore) => {
  const {
    storeSelectors,
    recordSelectors,
    pageSelectors,
    blockSelectors,
    blocksSelectors,
    walkthroughSelectors,
  } = getInterfaceStoreSelectors(store);

  return Object.assign(store, {
    hooks: {
      ...storeSelectors,
      record: recordSelectors,
      page: pageSelectors,
      block: blockSelectors,
      blocks: blocksSelectors,
      walkthrough: walkthroughSelectors,
    },
  });
};

export type RunnerInterfaceStoreSelectors = ReturnType<typeof createInterfaceStoreSelectors>;
